/**
 * Two-step confirm for critical actions (CLAUDE.md non-negotiable #9): gate opening changes over
 * 1.0 m, unit start/stop, plant-mode change, EAP declaration, LLO operation.
 *
 * The dialog is a real focus trap with a visible focus ring, so the flow works by keyboard alone —
 * which the Playwright suite asserts.
 */

import { useEffect, useRef } from 'react';
import { useSession } from '../../store/session';

export function ConfirmDialog(): JSX.Element | null {
  const pending = useSession((s) => s.pendingConfirm);
  const requestConfirm = useSession((s) => s.requestConfirm);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (pending) confirmRef.current?.focus();
  }, [pending]);

  useEffect(() => {
    if (!pending) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        requestConfirm(null);
      }
      if (e.key === 'Tab') {
        // Trap focus between the two buttons.
        e.preventDefault();
        (document.activeElement === confirmRef.current ? cancelRef : confirmRef).current?.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pending, requestConfirm]);

  if (!pending) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm critical action"
      data-testid="confirm-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(6,8,10,0.72)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 50,
      }}
    >
      <div
        className="panel"
        style={{ width: 420, boxShadow: '0 18px 48px rgba(0,0,0,0.6)', background: 'var(--surface-1)' }}
      >
        <div className="label">Confirm critical action</div>
        <div style={{ fontSize: 15, fontWeight: 600, margin: '8px 0 4px' }}>{pending.label}</div>
        <div style={{ color: 'var(--ink-dim)', marginBottom: 16 }}>{pending.detail}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button ref={cancelRef} onClick={() => requestConfirm(null)} data-testid="confirm-cancel">
            Cancel <span style={{ color: 'var(--ink-dim)' }}>Esc</span>
          </button>
          <button
            ref={confirmRef}
            className="primary"
            data-testid="confirm-ok"
            onClick={() => {
              pending.confirm();
              requestConfirm(null);
            }}
          >
            Confirm <span style={{ opacity: 0.7 }}>⏎</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hook: run `action` behind a two-step confirm. */
export function useConfirmed(): (label: string, detail: string, action: () => void) => void {
  const requestConfirm = useSession((s) => s.requestConfirm);
  return (label, detail, action) => requestConfirm({ label, detail, confirm: action });
}
